import React from 'react';
import { Loader2, Check } from 'lucide-react';

type ModalStatus = 'loading' | 'success' | 'closed';

interface ClassCreationStatusModalProps {
    status: ModalStatus;
    isRecurring: boolean;
    onClose: () => void;
}

/**
 * A styled modal for showing class creation progress and confirmation.
 * Displays a loading spinner while creating, then a success checkmark.
 */
const ClassCreationStatusModal: React.FC<ClassCreationStatusModalProps> = ({
    status,
    isRecurring,
    onClose
}) => {
    if (status === 'closed') return null;

    const isLoading = status === 'loading';
    const title = isLoading
        ? (isRecurring ? 'Adding Recurring Classes...' : 'Adding Class...')
        : (isRecurring ? 'Recurring Classes Created' : 'Class Created');

    return (
        <div 
            className="fixed inset-0 flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4"
            style={{ zIndex: 60 }}
        >
            <div className="bg-slate-900 shadow-2xl w-full max-w-sm overflow-hidden border border-slate-700 rounded-xl">
                <div className="p-8 text-center space-y-6">
                    {/* Icon */}
                    <div className="flex justify-center">
                        {isLoading ? (
                            <div className="w-16 h-16 rounded-full bg-fuchsia-900/30 flex items-center justify-center border border-fuchsia-700/50">
                                <Loader2 size={32} className="text-fuchsia-400 animate-spin" />
                            </div>
                        ) : (
                            <div className="w-16 h-16 rounded-full bg-emerald-900/30 flex items-center justify-center border border-emerald-700/50 animate-in zoom-in duration-300">
                                <Check size={32} className="text-emerald-400" />
                            </div>
                        )}
                    </div>

                    {/* Title */}
                    <h3 className="font-serif text-xl text-slate-100">{title}</h3>

                    {/* Description */}
                    <p className="text-slate-400 text-sm">
                        {isLoading 
                            ? (isRecurring 
                                ? 'Creating weekly recurring classes...' 
                                : 'Creating your new class...')
                            : (isRecurring 
                                ? 'Your recurring classes have been scheduled.' 
                                : 'Your class is now on the schedule.')
                        }
                    </p>

                    {/* Close Button (only show when not loading) */}
                    {!isLoading && (
                        <button
                            onClick={onClose}
                            className="w-full py-3 bg-fuchsia-700 hover:bg-fuchsia-600 text-white rounded-lg font-medium transition-colors"
                        >
                            Done
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ClassCreationStatusModal;
